/**
 * Past Score All Notes runs (COM-390), listed under the report filters.
 *
 * Deliberately separate from ScoreAllNotesPanel: that component owns the
 * RUNNING job's state and poll, so it stays mounted across the filter-form /
 * results switch. This list belongs with the filters and should disappear
 * with them, and it needs nothing from the running job -- just /list.
 *
 * Scope note, unchanged: this only resurfaces exact prior jobs by id. There is
 * no date-range-overlap detection or partial-result merging (backend-deferred).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Spinner } from 'reactstrap';

const RUN_LIST_STYLE = {
  border: '1px solid #e3e6ec',
  borderRadius: 8,
  overflow: 'hidden',
};

const RUN_ROW_STYLE = {
  alignItems: 'center',
  background: 'white',
  display: 'flex',
  gap: '0.875rem',
  padding: '0.7rem 0.875rem',
};

const formatCreatedAt = (createdAt) => {
  if (!createdAt) return '--';
  const date = new Date(createdAt);
  return Number.isNaN(date.getTime()) ? String(createdAt) : date.toLocaleString();
};

const RUNNING_STATUSES = ['queued', 'grading'];
const LIST_REFRESH_MS = 30000;

const NOTE_SOURCE_NAMES = {
  comprehend: 'Comprehend-graded only',
  non_comprehend: 'Clinician-authored only',
};

const RUN_PHASE_NAMES = {
  grading: 'Scoring',
  export: 'Building CSV',
  report: 'Building report',
};

// Which groups a run covered, by name (COM-564: "1 group" didn't say which).
const formatGroups = (labels) => {
  if (!Array.isArray(labels) || labels.length === 0) return 'All groups';
  if (labels.length <= 3) return labels.join(', ');
  return `${labels.slice(0, 3).join(', ')} +${labels.length - 3} more`;
};

// The run's criteria beyond the dates: rule set (by its document name when
// the clinic has one), clinicians picked, note source.
export const formatRunCriteria = (filters, ruleSetOptions = []) => {
  if (!filters) return '';
  const parts = [];
  if (Array.isArray(filters.user_emails) && filters.user_emails.length > 0) {
    parts.push(`${filters.user_emails.length} clinician${filters.user_emails.length === 1 ? '' : 's'}`);
  }
  const ruleSet = filters.export_rule_set;
  parts.push(
    ruleSet
      ? ruleSetOptions.find((option) => option.value === ruleSet)?.label || ruleSet
      : 'All rule sets',
  );
  if (filters.source && NOTE_SOURCE_NAMES[filters.source]) {
    parts.push(NOTE_SOURCE_NAMES[filters.source]);
  }
  return parts.join(' · ');
};

export const formatRunFilters = (filters) => {
  if (!filters) return 'All groups';
  const parts = [];
  // The backend normalizes the pair and always stores start_date <= end_date,
  // whichever way round the dashboard's t1/t2 pickers sent them.
  if (filters.start_date || filters.end_date) {
    // Parse as a LOCAL date: new Date('2026-09-25') is UTC midnight, which
    // renders as the 24th anywhere west of Greenwich.
    const asLocalDate = (value) => {
      const [year, month, day] = String(value).split('-').map(Number);
      return year && month && day
        ? new Date(year, month - 1, day).toLocaleDateString()
        : '?';
    };
    const start = filters.start_date ? asLocalDate(filters.start_date) : '?';
    const end = filters.end_date ? asLocalDate(filters.end_date) : '?';
    parts.push(`${start} – ${end}`);
  }
  parts.push(formatGroups(filters.labels));
  return parts.join(' · ');
};

const ScoreAllRunsList = ({
  apiClient, onViewRun, refreshKey, ruleSetOptions = [], viewingJobId = null,
}) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [downloadMessage, setDownloadMessage] = useState('');
  const [cancellingJobId, setCancellingJobId] = useState(null);

  const downloadRun = async (jobId) => {
    setDownloadingJobId(jobId);
    setDownloadMessage('');
    try {
      const exported = await apiClient.getScoreAllExport(jobId);
      if (exported?.status === 'ready' && exported.url) {
        window.location.assign(exported.url);
      } else if (exported?.status === 'failed' || exported?.error) {
        setDownloadMessage(exported.error || 'The export did not finish.');
      } else {
        setDownloadMessage("This file is still being prepared. Generate Analytics to track its progress.");
        await load();
      }
    } catch (downloadError) {
      setDownloadMessage('Unable to download the visit CSV. Please try again.');
    } finally {
      setDownloadingJobId(null);
    }
  };

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const response = await apiClient.listScoreAllRuns();
      if (response?.error) {
        throw new Error(response.error);
      }
      // /list returns a bare array; tolerate a {runs:[...]} wrapper too.
      const rows = Array.isArray(response) ? response : response?.runs;
      setRuns(Array.isArray(rows) ? rows : []);
    } catch (listError) {
      console.error('Unable to load recent score-all-notes runs:', listError);
      setError('Unable to load recent runs.');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  // refreshKey changes when a run finishes, so a completed run appears here
  // without the admin reloading the page.
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // A run started earlier (or in another tab) keeps moving on its runner, so
  // its row does too.
  const anyRunning = runs.some((run) => RUNNING_STATUSES.includes(run.status));
  useEffect(() => {
    if (!anyRunning) return undefined;
    const interval = window.setInterval(() => load({ quiet: true }), LIST_REFRESH_MS);
    return () => window.clearInterval(interval);
  }, [anyRunning, load]);

  const cancelRun = async (jobId) => {
    setCancellingJobId(jobId);
    setDownloadMessage('');
    try {
      const response = await apiClient.cancelScoreAllRun(jobId);
      if (response?.error) throw new Error(response.error);
      await load({ quiet: true });
    } catch (cancelError) {
      setDownloadMessage(
        `Couldn't cancel this run: ${cancelError?.serverMessage || cancelError?.message || 'try again.'}`,
      );
    } finally {
      setCancellingJobId(null);
    }
  };

  if (!apiClient?.features?.score_all_notes) {
    return null;
  }

  // Laid out as one more block of the filter form it sits under
  // (.filter-block* styles live in views/AnalyticsDashboard.js).
  return (
    <section className="filter-block recent-runs">
      <div className="filter-block-label">
        <h5>Recent exports</h5>
        <p>Download a visit CSV or view the analytics for a previous request.</p>
      </div>
      <div className="filter-block-body">
        {downloadMessage && <p className="filter-empty" role="status">{downloadMessage}</p>}
        {loading ? (
          <p className="filter-empty">
            <Spinner size="sm" style={{ marginRight: '8px' }} />
            Loading recent runs...
          </p>
        ) : error ? (
          <p className="filter-empty text-danger">{error}</p>
        ) : runs.length === 0 ? (
          <p className="filter-empty">No scoring runs yet.</p>
        ) : (
          <div style={RUN_LIST_STYLE}>
            {runs.map((run, index) => (
              <div
                key={run.jobId}
                style={{
                  ...RUN_ROW_STYLE,
                  borderTop: index === 0 ? 'none' : '1px solid #eef0f4',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: '#32325d', fontSize: '0.84rem', fontWeight: 600 }}>
                    {formatRunFilters(run.filters)}
                  </div>
                  <div style={{ color: '#525f7f', fontSize: '0.78rem' }}>
                    {formatRunCriteria(run.filters, ruleSetOptions)}
                  </div>
                  <div style={{ color: '#8898aa', fontSize: '0.75rem' }}>
                    Requested {formatCreatedAt(run.createdAt)}
                    {run.status === 'completed' && !run.reportKey
                      ? ' · no saved report'
                      : ''}
                  </div>
                </div>
                <Badge
                  pill
                  color={
                    run.status === 'completed'
                      ? 'success'
                      : run.status === 'failed'
                        ? 'danger'
                        : 'secondary'
                  }
                  style={{ marginLeft: 'auto', textTransform: 'capitalize' }}
                >
                  {run.status === 'grading'
                    ? RUN_PHASE_NAMES[run.progress?.phase] || 'Scoring'
                    : String(run.status || 'unknown').replaceAll('_', ' ')}
                </Badge>
                {RUNNING_STATUSES.includes(run.status) && (
                  <Button
                    type="button"
                    color="secondary"
                    outline
                    size="sm"
                    disabled={cancellingJobId === run.jobId}
                    onClick={() => cancelRun(run.jobId)}
                  >
                    {cancellingJobId === run.jobId ? 'Cancelling...' : 'Cancel'}
                  </Button>
                )}
                {run.status === 'completed' && run.exportKey && (
                <Button
                  type="button"
                  color="success"
                  size="sm"
                  disabled={run.status !== 'completed' || !!downloadingJobId}
                  onClick={() => downloadRun(run.jobId)}
                >
                  {downloadingJobId === run.jobId ? 'Checking Visit CSV...' : 'Download Raw Visit CSV'}
                </Button>
                )}
                <Button
                  type="button"
                  color="primary"
                  outline
                  size="sm"
                  disabled={run.status !== 'completed' || viewingJobId === run.jobId}
                  onClick={() => onViewRun?.(run.jobId, run.filters)}
                  style={{ minWidth: 64, borderColor: '#5e72e4', color: '#5e72e4' }}
                >
                  {viewingJobId === run.jobId ? 'Opening...' : 'View'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ScoreAllRunsList;
